import { Button } from "@/components/ui/button";
import { MutingVideo } from "@/components/shared/muting-media";
import { useToast } from "@/components/ui/use-toast";
import { type Call, useBackupCall } from "@/lib/api/calls";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { FileText, Loader2, Video, X } from "lucide-react";
import { useState } from "react";

const NO_RECORDING = "no recording";

export function hasCallRecording(call: Call): boolean {
  return Boolean(call.recording_url) && call.recording_url !== NO_RECORDING;
}

export function hasCallTranscript(call: Call): boolean {
  return Boolean(call.transcript && call.transcript.trim());
}

interface CallRecordingControlsProps {
  call: Call;
  className?: string;
}

/** Shared fetch / view / signed player for call recordings. */
export function CallRecordingControls({
  call,
  className,
}: CallRecordingControlsProps) {
  const { toast } = useToast();
  const { mutateAsync: fetchRecording, isPending } = useBackupCall();
  const [showPlayer, setShowPlayer] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const recordingAvailable = hasCallRecording(call);
  const transcriptAvailable = hasCallTranscript(call);

  const handleFetchRecording = async () => {
    toast({
      title: "Fetching recording...",
      description: "This may take a few seconds",
      variant: "default",
    });

    try {
      await fetchRecording({ callId: call.id });
      toast({
        title: "Recording fetched",
        description: "The recording has been fetched successfully",
        variant: "default",
      });
    } catch {
      toast({
        title: "Recording is not available",
        description: "Please try again later or contact support.",
        variant: "default",
      });
    }
  };

  return (
    <div className={className}>
      <div className="mt-1 flex gap-2">
        {recordingAvailable ? (
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => setShowPlayer((v) => !v)}
          >
            <Video className="mr-2 h-4 w-4" /> View recording
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={handleFetchRecording}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Fetch recording
          </Button>
        )}

        {transcriptAvailable ? (
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => setShowTranscript((v) => !v)}
          >
            <FileText className="mr-2 h-4 w-4" /> View transcript
          </Button>
        ) : null}
      </div>

      {showPlayer && recordingAvailable && call.recording_url ? (
        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-body text-body-3 font-medium text-text-primary">
              Recording
            </span>
            <button
              type="button"
              aria-label="Close recording"
              onClick={() => setShowPlayer(false)}
              className="text-text-tertiary hover:text-text-primary"
            >
              <X className="size-4" />
            </button>
          </div>
          <CallRecordingPlayer recordingUrl={call.recording_url} />
        </div>
      ) : null}

      {showTranscript && transcriptAvailable ? (
        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-body text-body-3 font-medium text-text-primary">
              Transcript
            </span>
            <button
              type="button"
              aria-label="Close transcript"
              onClick={() => setShowTranscript(false)}
              className="text-text-tertiary hover:text-text-primary"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-md border border-surfaces-divider bg-surfaces-z3 p-3">
            <p className="whitespace-pre-wrap font-body text-body-3 text-text-primary">
              {call.transcript}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CallRecordingPlayer({ recordingUrl }: { recordingUrl: string }) {
  const { url, isPending, isError } = useSignedMedia(recordingUrl);

  if (isPending) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (isError || !url) {
    return (
      <p className="font-body text-body-3 text-text-tertiary">
        Error loading video
      </p>
    );
  }

  return (
    <MutingVideo src={url} controls className="w-full rounded-md">
      Your browser does not support the video tag.
    </MutingVideo>
  );
}
